import { useState } from "react";
import { Link } from "react-router-dom";

import { applications, websites, games } from './ProjectData.js';
import Layout from '../../components/Layout.js';

import "./Projects.css";

// ── TAG FILTERING ─────────────────────────────────────────────

function getAllTags() {
    const tagSet = new Set();
    [...applications, ...websites, ...games].forEach(p =>
        p.tags.forEach(t => tagSet.add(t))
    );
    return Array.from(tagSet).sort();
}

function filterAndSort(projects, selectedTags) {
    if (selectedTags.length === 0) return projects;
    return projects
        .filter(p => selectedTags.some(t => p.tags.includes(t)))
        .sort((a, b) => {
            const aM = a.tags.filter(t => selectedTags.includes(t)).length;
            const bM = b.tags.filter(t => selectedTags.includes(t)).length;
            return bM - aM;
        });
}

// ── PROJECT CARD ──────────────────────────────────────────────

function ProjectCard({ path, image, title, tags, year }) {
    return (
        <Link to={`/projects/${path}`} className="proj-card">
            <div className="proj-card-img-wrap">
                <img
                    src={image}
                    alt={title}
                    className="proj-card-img"
                    loading="lazy"
                />
            </div>
            <div className="proj-card-body">
                {/* <div className="proj-card-meta">
                    {year && <span className="proj-card-year">{year}</span>}
                </div> */}
                <h3 className="proj-card-title">{title}</h3>
                <div className="proj-card-tags">
                    {tags.slice(0, 4).map(t => (
                        <span className="tech-pill" key={t}>{t}</span>
                    ))}
                    {tags.length > 4 && (
                        <span className="tech-pill proj-card-more">+{tags.length - 4}</span>
                    )}
                </div>
            </div>
        </Link>
    );
}

// ── TAG FILTER ────────────────────────────────────────────────

function TagFilter({ allTags, selectedTags, setSelectedTags }) {
    const [isOpen, setIsOpen] = useState(false);

    const toggle = tag =>
        setSelectedTags(prev =>
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );

    return (
        <div className="tag-filter">
            <button
                type="button"
                className="tag-filter-toggle"
                onClick={() => setIsOpen(o => !o)}
                aria-expanded={isOpen}
            >
                Filter by tag
                {selectedTags.length > 0 && (
                    <span className="tag-filter-count">{selectedTags.length}</span>
                )}
                <ion-icon
                    name="chevron-down-outline"
                    className={`tag-filter-chevron ${isOpen ? 'is-open' : ''}`}
                />
            </button>

            {isOpen && (
                <div className="tag-filter-wrap">
                    {allTags.map(tag => (
                        <button
                            key={tag}
                            className={`tag-filter-btn ${selectedTags.includes(tag) ? 'active' : ''}`}
                            onClick={() => toggle(tag)}
                        >
                            {tag}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── SECTION ───────────────────────────────────────────────────

function ProjectSection({ eyebrow, title, projects }) {
    if (projects.length === 0) return null;
    return (
        <section className="proj-section">
            <p className="section-eyebrow">{eyebrow}</p>
            <h2 className="section-title">{title}</h2>
            <div className="proj-grid">
                {projects.map(p => <ProjectCard key={p.path} {...p} />)}
            </div>
        </section>
    );
}

// ── PAGE ──────────────────────────────────────────────────────

function Projects() {
    const [selectedTags, setSelectedTags] = useState([]);
    const allTags = getAllTags();

    const isFiltering = selectedTags.length > 0;
    const allProjects = [...applications, ...websites, ...games];
    const filtered    = filterAndSort(allProjects, selectedTags);

    return (
        <Layout pageIndex={3}>
            {/* ── HERO ─────────────────────────────────── */}
            <div className="proj-hero page-hero noise">
                <div className="proj-header">
                    <p className="section-eyebrow">What I've built</p>
                    <h1 className="proj-page-title">Projects</h1>
                    <p className="proj-page-sub">
                        {allProjects.length} projects across applications, websites, and games.
                    </p>
                </div>
            </div>

            <div className="proj-outer">

                {/* ── TAG FILTER ───────────────────────────── */}
                <TagFilter
                    allTags={allTags}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                />

                {/* ── RESULTS ──────────────────────────────── */}
                {isFiltering ? (
                    <section className="proj-section">
                        <p className="section-eyebrow">
                            {filtered.length} match{filtered.length !== 1 ? 'es' : ''}
                        </p>
                        <h2 className="section-title">
                            Filtered results
                            <button
                                className="proj-clear-btn"
                                onClick={() => setSelectedTags([])}
                            >
                                Clear filters ×
                            </button>
                        </h2>
                        <div className="proj-grid">
                            {filtered.map(p => <ProjectCard key={p.path} {...p} />)}
                        </div>
                    </section>
                ) : (
                    <>
                        <ProjectSection
                            eyebrow="Tools & research"
                            title="Applications"
                            projects={applications}
                        />

                        <span id="websites"  />

                        <hr/>

                        <ProjectSection
                            eyebrow="Full-stack & frontend"
                            title="Websites"
                            projects={websites}
                        />

                        <span id="games"  />
                        <hr />

                        <ProjectSection
                        
                            eyebrow="Game jams & side projects"
                            title="Games"
                            projects={games}
                        />
                    </>
                )}
            </div>
        </Layout>
    );
}

export default Projects;